/**
 * API Error Types
 * Comprehensive error classification for better error handling
 */

export enum ApiErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  CLIENT_ERROR = 'CLIENT_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR',
  NOT_FOUND_ERROR = 'NOT_FOUND_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Custom API Error Class
 * Provides detailed error information for debugging and user feedback
 */
export class ApiError extends Error {
  type: ApiErrorType;
  status?: number;
  code?: string;
  details?: any;
  timestamp: Date;

  constructor(
    message: string,
    type: ApiErrorType,
    status?: number,
    code?: string,
    details?: any
  ) {
    super(message);
    this.name = 'ApiError';
    this.type = type;
    this.status = status;
    this.code = code;
    this.details = details;
    this.timestamp = new Date();

    // Maintains proper stack trace for where error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  /**
   * Convert error to JSON for logging
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      type: this.type,
      status: this.status,
      code: this.code,
      details: this.details,
      timestamp: this.timestamp.toISOString(),
      stack: this.stack,
    };
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage(): string {
    switch (this.type) {
      case ApiErrorType.NETWORK_ERROR:
        return 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.';
      case ApiErrorType.TIMEOUT_ERROR:
        return 'La requête a pris trop de temps. Veuillez réessayer.';
      case ApiErrorType.SERVER_ERROR:
        return 'Une erreur serveur est survenue. Veuillez réessayer plus tard.';
      case ApiErrorType.AUTHENTICATION_ERROR:
        return 'Authentification requise. Veuillez vous connecter.';
      case ApiErrorType.AUTHORIZATION_ERROR:
        return 'Vous n\'avez pas la permission d\'effectuer cette action.';
      case ApiErrorType.NOT_FOUND_ERROR:
        return 'La ressource demandée n\'a pas été trouvée.';
      case ApiErrorType.VALIDATION_ERROR:
        return this.message || 'Les données fournies sont invalides.';
      default:
        return this.message || 'Une erreur inattendue est survenue.';
    }
  }
}

/**
 * API Response Type with improved error handling
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: ApiError;
  status?: number;
  message?: string;
  timestamp: Date;
}

/**
 * Request Options
 */
export interface RequestOptions {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  skipErrorHandling?: boolean;
  signal?: AbortSignal;
}
